import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useDispatch, useSelector } from 'react-redux';
import {
    setCommandMessage,
    resetOperation,
    setOperationData,
    setOperationStep,
} from '../../../redux/operationSlice';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { COMMANDS } from '../../../utils/commonProperties';
import { createDrawingClickHandler, applyGridSnap } from "../../../modules/drawing";
import { getSnappedPoint } from '../../../shared/snap/getSnappedPoint';
import { createSnapMarker } from '../../../shared/snap/createSnapMarker';
import { Line2 } from 'three/examples/jsm/lines/Line2';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';
import { selectAllFloorPlans, addTesisatIdToFloorPlan } from '../../../redux/floorPlanSlice';
import { addTesisat } from '../../../redux/tesisatSlice';
import { getActiveFloorPlanId, getGlobalDocumentBounds } from '../../../utils/getActiveFloorPlanId';
import { detectColumns, calculateFloorOffset, visualizeColumns, clearColumnOverlays,
         detectStairs, calculateFloorOffsetByStairs, findCorrespondingStair } from '../../../utils/columnDetection';
import { findFloorPlanMeshById, attachTesisatToFloorPlanMesh } from '../../../modules/fpSync';
import { createDiagonalLine, createExitPoint, getPointOnDiagonal } from '../../../modules/wiring';

const useCableShaft = (scene, camera, renderer, snapPoints) => {
    const snapMarkerRef = useRef(null);
    const tempRectRef = useRef(null);
    const dispatch = useDispatch();

    const { commandType, step, data } = useSelector(state => state.operation);
    const gridMode = useSelector(state => state.mods.gridMode);
    const objectSnap = useSelector(state => state.mods.objectSnap);
    const snapMode = useSelector((state) => state.mods.snapMode);
    const floorPlans = useSelector(selectAllFloorPlans);
    const serializeVector = (v) => ({ x: v.x, y: v.y, z: v.z });

    // Temizleme yardımcı fonksiyonu
    const disposeObject = (obj) => {
        // Grup ise children'ları temizle
        if (obj.isGroup && obj.children) {
            obj.children.forEach(child => disposeObject(child));
        }
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
            if (Array.isArray(obj.material)) {
                obj.material.forEach(m => m.dispose());
            } else {
                obj.material.dispose();
            }
        }
    };

    // Kablo bacası çizim fonksiyonu
    const createCableShaftObject = (corner1, corner2, color = 0x00ffff, options = {}) => {
        const minX = Math.min(corner1.x, corner2.x);
        const maxX = Math.max(corner1.x, corner2.x);
        const minY = Math.min(corner1.y, corner2.y);
        const maxY = Math.max(corner1.y, corner2.y);

        const width = maxX - minX;
        const height = maxY - minY;

        const shaftId = generateUniqueId();

        // Ana grup oluştur
        const group = new THREE.Group();

        // 1. Arka plan dolgu (tıklanabilir alan) - %30 opaklık (70% transparan)
        const fillShape = new THREE.Shape();
        fillShape.moveTo(minX, minY);
        fillShape.lineTo(maxX, minY);
        fillShape.lineTo(maxX, maxY);
        fillShape.lineTo(minX, maxY);
        fillShape.lineTo(minX, minY);

        const fillGeometry = new THREE.ShapeGeometry(fillShape);
        const fillMaterial = new THREE.MeshBasicMaterial({
            color: color,
            transparent: true,
            opacity: 0.3, // %30 opaklık = %70 transparan
            side: THREE.DoubleSide,
            depthWrite: false,
        });

        const fillMesh = new THREE.Mesh(fillGeometry, fillMaterial);
        fillMesh.position.z = 0.05; // Hafif yukarıda
        group.add(fillMesh);

        // 2. Border çizgisi (Line2 ile kalın çizgi)
        const rectPoints = [
            minX, minY, 0.1,
            maxX, minY, 0.1,
            maxX, maxY, 0.1,
            minX, maxY, 0.1,
            minX, minY, 0.1,
        ];

        const lineGeometry = new LineGeometry();
        lineGeometry.setPositions(rectPoints);

        const lineMaterial = new LineMaterial({
            color: color,
            linewidth: 3,
            resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
        });

        const line = new Line2(lineGeometry, lineMaterial);
        line.computeLineDistances();
        group.add(line);

        // 3. Çapraz çıkış çizgisi (kullanıcının tıkladığı iki nokta arasında)
        // corner1 ve corner2 kullanıcının tıkladığı noktalardır
        const diagonalLine = createDiagonalLine(corner1, corner2, shaftId);
        diagonalLine.userData.isShaftPart = true;
        group.add(diagonalLine);

        // ✅ Başlangıçta çıkış noktası YOK - kablo geldiğinde dinamik olarak oluşturulacak

        // Grup userData (tıklama için gerekli)
        group.userData = {
            id: shaftId,
            isSelectable: true,
            type: "cableShaft",
            start: { x: minX, y: minY, z: 0 },
            width: width,
            height: height,
            rotation: 0,
            points: [
                { x: minX, y: minY, z: 0 },
                { x: maxX, y: minY, z: 0 },
                { x: maxX, y: maxY, z: 0 },
                { x: minX, y: maxY, z: 0 },
            ],
            center: { x: (minX + maxX) / 2, y: (minY + maxY) / 2, z: 0 },
            // Çapraz çizgi bilgileri (kullanıcının tıkladığı noktalar)
            diagonalStart: { x: corner1.x, y: corner1.y },
            diagonalEnd: { x: corner2.x, y: corner2.y },
            exitCount: 0, // ✅ Başlangıçta 0 çıkış noktası
            occupiedExitCount: 0, // Dolu çıkış sayısı
            // Kablo bacası bağlantı verileri
            shaftGroupId: options.shaftGroupId || shaftId,
            floorPlanId: options.floorPlanId || null,
            floorPlanName: options.floorPlanName || null,
            connectedCables: [],
        };

        // Alt nesnelere de userData ekle (raycast için)
        fillMesh.userData = { ...group.userData, isFill: true };
        line.userData = { ...group.userData, isBorder: true };
        // ✅ Çapraz çizgide gizmo oluşmasın - isSelectable: false
        diagonalLine.userData = { ...diagonalLine.userData, ...group.userData, isDiagonalLine: true, isSelectable: false, isGizmo: false };

        return group;
    };

    // 2 noktalı çapraz köşe ile dikdörtgen çizimi (preview için)
    const drawCableShaftDiagonal = (corner1, corner2, color = 0x00ffff, isPreview = true, options = {}) => {
        const line = createCableShaftObject(corner1, corner2, color, options);

        if (isPreview) {
            // Preview modunda: eski preview'ı kaldır, yenisini sakla
            if (tempRectRef.current) {
                scene.remove(tempRectRef.current);
                disposeObject(tempRectRef.current);
            }
            tempRectRef.current = line;
        } else {
            // ✅ FIX: Final shaft oluşturulurken preview'ı temizle
            if (tempRectRef.current) {
                scene.remove(tempRectRef.current);
                disposeObject(tempRectRef.current);
                tempRectRef.current = null;
            }
        }

        scene.add(line);
        return line;
    };

    // Yardımcı: kolon/merdiven listelerini fpMesh-local koord uzayına çevir
    const toLocalPoint = (mesh, p) => {
        if (!p) return p;
        const v = new THREE.Vector3(p.x, p.y, 0);
        mesh.worldToLocal(v);
        return { x: v.x, y: v.y };
    };
    const columnsToLocal = (mesh, cols) => cols.map(c => ({
        ...c,
        center: toLocalPoint(mesh, c.center),
    }));
    const stairsToLocal = (mesh, stairs) => stairs.map(s => ({
        ...s,
        center: toLocalPoint(mesh, s.center),
        anchorStart: toLocalPoint(mesh, s.anchorStart),
        anchorEnd: toLocalPoint(mesh, s.anchorEnd),
    }));

    // Diğer katlara otomatik kablo bacası yerleştirme — mesh-FP yapısı:
    // tüm hesaplama fpMesh-local koord uzayında yapılır. Aynı mimari plan farklı
    // katlarda fpMesh-local'de aynı kolon pozisyonlarına sahiptir → layout offset
    // hesaba katılmaz, kat sürüklemesi etkilemez. Sonuçta projection_local
    // hedef fpMesh.localToWorld ile world coord'a çevrilir, attach edilir.
    const placeCableShaftsOnOtherFloors = (cableShaftCenter, cableShaftWidth, cableShaftHeight, currentFloorPlanId, shaftGroupId) => {
        if (!floorPlans || floorPlans.length < 2) {
            return;
        }

        const currentFpMesh = findFloorPlanMeshById(scene, currentFloorPlanId);
        if (!currentFpMesh) {
            console.warn('[Cable Shaft] Current fpMesh not found in scene');
            return;
        }
        // Matrix güncellemesi — worldToLocal güncel matrisi okusun
        currentFpMesh.updateWorldMatrix(true, false);

        // Click world → current fpMesh local
        const clickLocal = toLocalPoint(currentFpMesh, cableShaftCenter);

        // Detection world coord döner; fpMesh-local'e çevir
        const currentColumnsWorld = detectColumns(currentFpMesh);
        const currentColumns = columnsToLocal(currentFpMesh, currentColumnsWorld);
        const currentStairsWorld = currentColumns.length < 4 ? detectStairs(currentFpMesh) : [];
        const currentStairs = stairsToLocal(currentFpMesh, currentStairsWorld);
        const useStairs = currentColumns.length < 4;

        if (useStairs && currentStairs.length === 0) {
            console.warn('[Cable Shaft] Neither columns nor stairs detected on current floor');
            dispatch(setCommandMessage("Kolon veya merdiven bulunamadı. Diğer katlara otomatik yerleştirme yapılamadı."));
            return;
        }

        // Görselleştirme (world coord'da yapılır — bilgilendirici overlay)
        if (!useStairs) {
            visualizeColumns(scene, currentColumnsWorld, 0xff00ff, 0.5);
        }

        // Local space'de click'e en yakın referans (kolon veya merdiven)
        const refPoints = useStairs ? currentStairs : currentColumns;
        const sortedByDistance = [...refPoints].sort((a, b) => {
            const distA = Math.hypot(a.center.x - clickLocal.x, a.center.y - clickLocal.y);
            const distB = Math.hypot(b.center.x - clickLocal.x, b.center.y - clickLocal.y);
            return distA - distB;
        });
        const nearestRef = sortedByDistance[0];
        const nearestAnchor = nearestRef.center;
        const anchorKind = 'center';

        // relativeOffset: click - nearestRef (local space)
        const relativeOffset = {
            x: clickLocal.x - nearestAnchor.x,
            y: clickLocal.y - nearestAnchor.y,
        };

        let placedCount = 0;

        for (const otherFloorPlan of floorPlans) {
            if (otherFloorPlan.id === currentFloorPlanId) continue;

            const otherFpMesh = findFloorPlanMeshById(scene, otherFloorPlan.id);
            if (!otherFpMesh) continue;
            otherFpMesh.updateWorldMatrix(true, false);

            let correspondingRefLocal = null;

            if (!useStairs) {
                const otherColumnsWorld = detectColumns(otherFpMesh);
                if (otherColumnsWorld.length < 4) {
                    console.warn(`[Cable Shaft] Not enough columns on ${otherFloorPlan.name}`);
                    continue;
                }
                visualizeColumns(scene, otherColumnsWorld, 0x00ffff, 0.5);
                const otherColumns = columnsToLocal(otherFpMesh, otherColumnsWorld);

                // Aynı mimari plan farklı katlarda fpMesh-local'de neredeyse aynı
                // kolon pozisyonlarına sahip. calculateFloorOffset best-match offset'i
                // bulur; ideal durumda offset≈(0,0) ama partial detection veya
                // küçük plan farklılıkları için yine de bu uyumlanır.
                const floorOffset = calculateFloorOffset(currentColumns, otherColumns);
                if (!floorOffset) {
                    console.warn(`[Cable Shaft] Could not calculate column offset for ${otherFloorPlan.name}`);
                    continue;
                }
                correspondingRefLocal = {
                    x: nearestRef.center.x + floorOffset.offsetX,
                    y: nearestRef.center.y + floorOffset.offsetY,
                };
            } else {
                const otherStairsWorld = detectStairs(otherFpMesh);
                if (otherStairsWorld.length === 0) {
                    console.warn(`[Cable Shaft] No stairs on ${otherFloorPlan.name}`);
                    continue;
                }
                const otherStairs = stairsToLocal(otherFpMesh, otherStairsWorld);
                const counterpart = findCorrespondingStair(nearestRef, otherStairs);
                if (counterpart) {
                    const target = counterpart[anchorKind] || counterpart.center;
                    correspondingRefLocal = { x: target.x, y: target.y };
                } else {
                    const floorOffset = calculateFloorOffsetByStairs(currentStairs, otherStairs);
                    if (!floorOffset) {
                        console.warn(`[Cable Shaft] Could not calculate stair offset for ${otherFloorPlan.name}`);
                        continue;
                    }
                    correspondingRefLocal = {
                        x: nearestAnchor.x + floorOffset.offsetX,
                        y: nearestAnchor.y + floorOffset.offsetY,
                    };
                }
            }

            // Projection in target fpMesh-local
            const projectionLocal = {
                x: correspondingRefLocal.x + relativeOffset.x,
                y: correspondingRefLocal.y + relativeOffset.y,
            };

            // Target fpMesh local → world
            const projectionWorldV = new THREE.Vector3(projectionLocal.x, projectionLocal.y, 0);
            otherFpMesh.localToWorld(projectionWorldV);
            const newCableShaftCenter = { x: projectionWorldV.x, y: projectionWorldV.y };

            const halfWidth = cableShaftWidth / 2;
            const halfHeight = cableShaftHeight / 2;
            const newCorner1 = { x: newCableShaftCenter.x - halfWidth, y: newCableShaftCenter.y - halfHeight };
            const newCorner2 = { x: newCableShaftCenter.x + halfWidth, y: newCableShaftCenter.y + halfHeight };

            const newCableShaft = createCableShaftObject(newCorner1, newCorner2, 0x00ffff, {
                shaftGroupId: shaftGroupId,
                floorPlanId: otherFloorPlan.id,
                floorPlanName: otherFloorPlan.name,
            });
            newCableShaft.userData.autoPlaced = true;
            newCableShaft.userData.sourceFloorPlanId = currentFloorPlanId;

            // Redux persistence — current floor ile aynı mantık
            const nud = newCableShaft.userData;
            dispatch(addTesisat({
                id: nud.id,
                type: 'cableShaft',
                position: { x: 0, y: 0, z: 0 },
                rotation: 0,
                floorPlanId: otherFloorPlan.id,
                corner1: { x: nud.diagonalStart.x, y: nud.diagonalStart.y },
                corner2: { x: nud.diagonalEnd.x, y: nud.diagonalEnd.y },
                shaftGroupId: nud.shaftGroupId,
                floorPlanName: nud.floorPlanName,
                _v: 2,
            }));
            dispatch(addTesisatIdToFloorPlan({
                tabId: 'elektrik',
                floorPlanId: otherFloorPlan.id,
                itemId: nud.id,
            }));

            attachTesisatToFloorPlanMesh(scene, newCableShaft, otherFloorPlan.id);
            placedCount++;
        }

        if (placedCount > 0) {
            const refLabel = useStairs ? 'merdiven' : 'kolon';
            dispatch(setCommandMessage(`Kablo bacası ${placedCount} kata daha yerleştirildi (${refLabel} referansı).`));
            // Mesajı 3 saniye sonra temizle
            setTimeout(() => {
                dispatch(setCommandMessage(""));
            }, 3000);
        }

        // Debug overlayleri 5 saniye sonra temizle
        setTimeout(() => {
            clearColumnOverlays(scene);
        }, 5000);
    };

    useEffect(() => {
        if (commandType !== COMMANDS.DRAW_CABLE_SHAFT || !scene || !camera || !renderer) return;

        const domElement = renderer.domElement;
        dispatch(setCommandMessage("Kablo bacası için ilk köşeyi seçin."));

        const handleCableShaftPoint = (point) => {
            if (step === 0) {
                dispatch(setOperationData({ start: serializeVector(point) }));
                dispatch(setOperationStep(1));
                dispatch(setCommandMessage("Karşı köşeyi seçin."));
            } else if (step === 1) {
                const start = new THREE.Vector3(data.start.x, data.start.y, data.start.z);

                // ✅ DEBUG: Log existing shafts BEFORE creating new one
                const existingShafts = [];
                scene.traverse((obj) => {
                    if (obj.userData?.type === 'cableShaft' && !obj.userData?.isFill && !obj.userData?.isBorder) {
                        existingShafts.push({
                            id: obj.userData.id,
                            shaftGroupId: obj.userData.shaftGroupId,
                            floorPlanId: obj.userData.floorPlanId,
                            floorPlanName: obj.userData.floorPlanName
                        });
                    }
                });
                // Hangi kat planında olduğunu bul (önce hesapla, sonra kablo bacasını oluştur)
                const bounds = getGlobalDocumentBounds();
                const tempCenter = {
                    x: (start.x + point.x) / 2,
                    y: (start.y + point.y) / 2,
                };
                const centerPoint = new THREE.Vector3(tempCenter.x, tempCenter.y, 0);
                const currentFloorPlanId = getActiveFloorPlanId(centerPoint, bounds);

                // Kat planı adını bul
                const currentFloorPlan = floorPlans.find(fp => fp.id === currentFloorPlanId);
                const currentFloorPlanName = currentFloorPlan?.name || 'Bilinmeyen Kat';

                // Kablo bacasını çiz (floorPlanId ve floorPlanName ile)
                const cableShaft = drawCableShaftDiagonal(start, point, 0x00ffff, false, {
                    floorPlanId: currentFloorPlanId,
                    floorPlanName: currentFloorPlanName,
                });
                // Redux persistence: cableShaft otomatik sync mekanizması yok
                // (useSceneTesisatSync supplyCounter/items dep'lerine bağlı,
                // shaft creation hiçbirini tetiklemez). Doğrudan dispatch et —
                // tesisatSlice'a düşsün ki save/reload doğru çalışsın.
                if (currentFloorPlanId) {
                    const ud = cableShaft.userData;
                    dispatch(addTesisat({
                        id: ud.id,
                        type: 'cableShaft',
                        position: { x: 0, y: 0, z: 0 },
                        rotation: 0,
                        floorPlanId: currentFloorPlanId,
                        corner1: { x: ud.diagonalStart.x, y: ud.diagonalStart.y },
                        corner2: { x: ud.diagonalEnd.x, y: ud.diagonalEnd.y },
                        shaftGroupId: ud.shaftGroupId,
                        floorPlanName: ud.floorPlanName,
                        _v: 2,
                    }));
                    dispatch(addTesisatIdToFloorPlan({
                        tabId: 'elektrik',
                        floorPlanId: currentFloorPlanId,
                        itemId: ud.id,
                    }));
                    // Mesh-FP: bacayı kendi fpMesh'ine attach et — kat sürüklenince
                    // baca da takip eder.
                    // Mesh-FP: bacayı kendi fpMesh'ine attach et — kat sürüklenince
                    // baca da takip eder.
                    attachTesisatToFloorPlanMesh(scene, cableShaft, currentFloorPlanId);
                }
                const cableShaftData = cableShaft.userData;

                if (currentFloorPlanId) {
                    // Diğer katlara otomatik yerleştir (aynı shaftGroupId ile)
                    placeCableShaftsOnOtherFloors(
                        cableShaftData.center,
                        cableShaftData.width,
                        cableShaftData.height,
                        currentFloorPlanId,
                        cableShaftData.shaftGroupId // Ana bacanın shaftGroupId'si
                    );
                } else {
                    console.warn('[Cable Shaft] Could not determine floor plan - auto-placement SKIPPED to prevent duplicates');
                    dispatch(setCommandMessage("Kat planı tespit edilemedi. Kablo bacasını bir kat planı içinde oluşturun."));
                }

                dispatch(resetOperation());
                dispatch(setCommandMessage(""));
            }
        };

        const handleClick = createDrawingClickHandler({
            scene,
            camera,
            renderer,
            snapPoints,
            snapMarkerRef,
            gridMode,
            objectSnap,
            snapMode,
            onPointSelected: handleCableShaftPoint
        });

        const handleMouseMove = (e) => {
            const rect = domElement.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
            const mouseNDC = new THREE.Vector2(x, y);
            const worldPoint = new THREE.Vector3(x, y, 0).unproject(camera);

            const { finalPoint, snapped, snapSource } = getSnappedPoint(
                mouseNDC,
                worldPoint,
                snapPoints,
                camera,
                renderer,
                { snapMode }
            );
            const point = (objectSnap && snapped) ? finalPoint.clone() : (gridMode ? applyGridSnap(worldPoint) : worldPoint.clone());

            if (snapMarkerRef.current) {
                scene.remove(snapMarkerRef.current);
                snapMarkerRef.current.geometry.dispose();
                snapMarkerRef.current.material.dispose();
                snapMarkerRef.current = null;
            }

            if (snapped && snapSource) {
                const marker = createSnapMarker(8, snapSource);
                marker.position.copy(finalPoint);
                scene.add(marker);
                snapMarkerRef.current = marker;
            }

            // step 1: ilk köşe seçildikten sonra önizleme göster
            if (step === 1 && data.start) {
                const start = new THREE.Vector3(data.start.x, data.start.y, data.start.z);
                drawCableShaftDiagonal(start, point, 0x00ffff, true);
            }
        };

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (tempRectRef.current) {
                    scene.remove(tempRectRef.current);
                    disposeObject(tempRectRef.current);
                    tempRectRef.current = null;
                }
                if (snapMarkerRef.current) {
                    scene.remove(snapMarkerRef.current);
                    snapMarkerRef.current.geometry?.dispose();
                    snapMarkerRef.current.material?.dispose();
                    snapMarkerRef.current = null;
                }
                dispatch(resetOperation());
                dispatch(setCommandMessage(""));
            }
        };

        domElement.addEventListener('click', handleClick);
        domElement.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            domElement.removeEventListener('click', handleClick);
            domElement.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [commandType, step, data, scene, camera, renderer, dispatch, gridMode, snapMode, objectSnap, floorPlans]);
};

export default useCableShaft;
